import { createContext, FC } from 'react';
import PublicServiceDrawer from '@/components/drawers/public-service';
import PrivateModelDrawer from '@/components/drawers/private-model';
import AdminUserDrawer from '@/components/drawers/admin-user';
import UserDrawer from '@/components/drawers/end-user';
import GroupDrawer from '@/components/drawers/group';
import UserEventDrawer from '@/components/drawers/user-event';
import ConnectorDrawer from '@/components/drawers/connector';
import ContextProviderDrawer from '@/components/drawers/context-provider';
import TelemetryDrawer from '@/components/drawers/telemetry';
import SdEntityDrawer from '@/components/drawers/sd-entity';
import ClassifierDrawer from '@/components/drawers/classifier';
import AuditLogDrawer from '@/components/drawers/audit-log';
import McpServerDrawer from '@/components/drawers/mcp-server';
import ConversationDrawer from '@/components/drawers/conversation';
import PolicyDrawer from '@/components/drawers/policy';
import NewWithNameDrawer from '@/components/drawers/new-with-name';
import UserEventExportDrawer from '@/components/drawers/user-event-export';
import { getQueryStringParams, patchQueryParams } from '@/lib/url-helpers';
import { JsonObject } from '@/lib/helpers';
import { TabVariant } from '@/hooks/use-tabs.hook';
import { DrawerProps } from '@/components/drawers/helpers';

export type DrawerType =
  | 'context-provider'
  | 'user-event'
  | 'user-event-export'
  | 'public-service'
  | 'private-model'
  | 'admin-user'
  | 'end-user'
  | 'group'
  | 'connector'
  | 'telemetry'
  | 'sd-entity'
  | 'classifier'
  | 'mcp-server'
  | 'audit-log'
  | 'conversation'
  | 'policy'
  | 'new-with-name';

export const drawerMap: Map<DrawerType, FC> = new Map();
drawerMap.set('context-provider', ContextProviderDrawer);
drawerMap.set('user-event', UserEventDrawer);
drawerMap.set('user-event-export', UserEventExportDrawer);
drawerMap.set('public-service', PublicServiceDrawer);
drawerMap.set('private-model', PrivateModelDrawer);
drawerMap.set('admin-user', AdminUserDrawer);
drawerMap.set('end-user', UserDrawer);
drawerMap.set('group', GroupDrawer);
drawerMap.set('connector', ConnectorDrawer);
drawerMap.set('telemetry', TelemetryDrawer);
drawerMap.set('sd-entity', SdEntityDrawer);
drawerMap.set('classifier', ClassifierDrawer);
drawerMap.set('mcp-server', McpServerDrawer);
drawerMap.set('audit-log', AuditLogDrawer);
drawerMap.set('conversation', ConversationDrawer);
drawerMap.set('policy', PolicyDrawer);
drawerMap.set('new-with-name', NewWithNameDrawer);

export type DrawerConfig = {
  drawerType: DrawerType;
  props: DrawerProps;
  closing?: 'all' | 'self';
  mounted: boolean;
  onClose: () => void;
};

export interface IDrawerState {
  openDrawer: (drawerType: DrawerType, addlProps?: Record<string, unknown>) => void;
  closeDrawer: (force?: boolean) => void;
  goBackDrawer: (force?: boolean) => void;
  stack: DrawerConfig[];
}

export const DrawerContext = createContext<IDrawerState>({
  openDrawer: () => null,
  closeDrawer: () => null,
  goBackDrawer: () => null,
  stack: [],
});

type DrawerUrlParams = { drawer: DrawerType } & Record<string, string | boolean>;

export const setUrlParams = (
  drawerType: DrawerType,
  drawerProps: JsonObject = {},
  clear?: boolean
) => {
  const urlParams: Record<string, string | null> = {
    drawer: drawerType,
  };

  const allowedParams = [
    'drawer',
    'tab',
    'id',
    'policyId',
    'conversationId',
    'requestId',
    'anchorActivityId',
    'title',
  ];

  allowedParams.forEach((key) => {
    let propVal = drawerProps[key];

    if (key === 'tab' && typeof propVal === 'string') {
      urlParams[TabVariant.drawer] = propVal;
      return;
    }

    // convert boolean to stringified version
    if (typeof propVal === 'boolean') {
      propVal = propVal ? 'true' : 'false';
    }

    if (typeof propVal === 'number') {
      propVal = String(propVal);
    }

    if (typeof propVal === 'string') {
      urlParams[`dp_${key}`] = propVal;
      return;
    }

    if (clear) {
      urlParams[`dp_${key}`] = null;
    }
  });

  patchQueryParams(urlParams, true);
};

export const clearUrlParams = () => {
  const urlParams = getQueryStringParams();

  const clearParams: Record<string, null> = {
    drawer: null,
  };

  Object.keys(urlParams)
    .filter((key) => key.startsWith('dp_'))
    .forEach((key) => {
      clearParams[key] = null;
    });

  patchQueryParams(clearParams, true);
};

export const getUrlParams = (drawerType?: DrawerType): DrawerUrlParams | null => {
  const { drawer: drawerParam, ...rest } = getQueryStringParams();
  if (drawerType && drawerParam !== drawerType) {
    return null;
  }

  if (!drawerMap.has(drawerParam as DrawerType)) {
    return null;
  }

  const drawer = drawerParam as DrawerType;
  const urlParams: DrawerUrlParams = { drawer };

  Object.keys(rest)
    .filter((key) => key.startsWith('dp_'))
    .forEach((key) => {
      let urlVal: string | boolean = rest[key];

      // convert stringified boolean back to type boolean
      if (['true', 'false'].includes(urlVal)) {
        urlVal = urlVal === 'true';
      }

      urlParams[key.replace(/^dp_/, '')] = urlVal;
    });

  return urlParams;
};
